/**
 * WebSocket liveness primitives, kept free of any react-native imports so they
 * can be unit-tested in the node/vitest environment (importing useGameSocket
 * directly drags in expo-modules-core, which references __DEV__).
 */

/** Client keepalive ping cadence. The server replies with a PONG to each. */
export const PING_INTERVAL_MS = 25_000
/** How often the liveness watchdog checks whether the socket has gone quiet. */
export const LIVENESS_CHECK_MS = 15_000
/**
 * If no message of ANY kind (data push or the server's PONG reply to our PING)
 * arrives within this window, the socket is treated as dead and force-reconnected.
 * Sized to allow ~2 missed ping/pong cycles so a single slow round-trip doesn't
 * trip it.
 */
export const LIVENESS_STALE_MS = 60_000

/**
 * A WebSocket can go "half-open": the OS still reports it OPEN and `onclose`
 * never fires, but no bytes flow in either direction (common on mobile when the
 * network transitions — the exact "delivers a few events then silently stops"
 * symptom). Since the server PONGs every client PING, the absence of ANY inbound
 * message for `staleMs` despite our pings means the socket is dead.
 */
export function isSocketStale(lastMessageAt: number, now: number, staleMs: number): boolean {
  return now - lastMessageAt > staleMs
}

/**
 * Decide how the game socket should react when its platform reports a
 * foreground/visibility change. Shared by the native AppState listener and the
 * web visibilitychange listener in useGameSocket:
 *
 *   - active + socket open → 'focus': re-assert focus to the server.
 *   - active + socket dead → 'reconnect': iOS suspends/closes the socket while
 *     backgrounded, and browsers throttle/freeze hidden tabs, so a resume must
 *     reconnect immediately rather than wait for the (throttled) retry timer
 *     or the liveness watchdog to notice. Otherwise the player returns to a
 *     dead socket: no live updates, and the server — seeing no live socket —
 *     keeps firing "your turn" pushes.
 *   - not active → 'blur': tell the server to stop suppressing notifications.
 */
export function focusTransitionAction(
  isActive: boolean,
  socketOpen: boolean,
): 'focus' | 'reconnect' | 'blur' {
  if (!isActive) return 'blur'
  return socketOpen ? 'focus' : 'reconnect'
}
